import { Quaternion, TransformNode } from '@babylonjs/core';
import type { Mesh, Scene, Vector3 } from '@babylonjs/core';

import type { DiagramSummaryDto, LobbyDiagramDto, NodeTypeDto } from '../generated/contracts';
import { buildLabelledPlate, disposePlate } from '../input/LabelledPlate';
import { DiagramMiniature } from './DiagramMiniature';
import { clampRotation, layout, NEAR_HEIGHT_METRES, rankCount } from './LobbyLayout';
import { ModelLibrary } from './ModelLibrary';

/**
 * How far above a miniature's own unit-box top edge its name label floats, in the miniature
 * root's own local units (`DiagramMiniature.fitToUnitBox`'s own doc comment: every diagram's
 * content is centred on the origin and stays within ±0.5 on every axis regardless of shape, so
 * "just above the box" is `0.5 + LABEL_GAP`, universally, with no per-diagram bounds read needed).
 */
const LABEL_GAP = 0.12;

/** In the same local units as LABEL_GAP — physical size follows from the miniature's own
 *  per-slot scale, since the label is parented to the miniature's root rather than to the room. */
const LABEL_WIDTH = 0.95;
const LABEL_HEIGHT = 0.24;

/** Matching LabelRenderer.ts's own neutral name-plate palette (`BACKGROUND`/`TEXT_COLOR`) rather
 *  than ExitSign's call-to-action pink — this is a name, not a button. */
const LABEL_BACKGROUND = '#12151aE6';
const LABEL_INK = '#E7ECF3';

/**
 * The lobby: your diagrams standing around you as live miniatures, and opening one is how a
 * session begins (SPEC-156, SPEC-162).
 *
 * **What makes this scale is what it declines to build, not what it hides.** `LobbyLayout` says
 * which slots are worth geometry; anything further back has a position and a name and no meshes
 * at all. A version that built every miniature and then set `isEnabled(false)` on the far ones
 * would pass the same visual inspection and fail the actual requirement — SPEC-157 is a claim
 * about cost, and an invisible mesh still costs its build, its materials and its memory.
 *
 * Summaries are fetched lazily, per diagram, only when a slot needs detail. The lobby list itself
 * (`GET /api/diagrams/lobby`) is deliberately flat and carries no geometry, precisely so arriving
 * in a lobby with three hundred diagrams is one small response rather than three hundred payloads.
 */
// spec: SPEC-156
// spec: SPEC-157
// spec: SPEC-162
// spec: SPEC-189
// spec: SPEC-201
export class LobbyView {
  readonly root: TransformNode;

  private diagrams: LobbyDiagramDto[] = [];

  private rotation = 0;

  private catalog: NodeTypeDto[] = [];

  /** Miniatures that currently exist, keyed by diagram id. Built and torn down as ranks move. */
  private readonly built = new Map<string, DiagramMiniature>();

  /** Each built miniature's own name label (SPEC-201) — built and torn down in lockstep with
   *  `built`, never longer-lived than the miniature it hangs above. */
  private readonly labels = new Map<string, Mesh>();

  /** Summaries already fetched, so rotating back and forth does not re-fetch. */
  private readonly summaries = new Map<string, DiagramSummaryDto>();

  /** In-flight fetches, so a rank arriving twice in quick succession asks once. */
  private readonly pending = new Map<string, Promise<void>>();

  private disposed = false;

  constructor(
    private readonly scene: Scene,
    parent: TransformNode,
    /** Fetches one diagram's summary — the same bounded payload the desktop viewer reads. */
    private readonly fetchSummary: (diagramId: string) => Promise<DiagramSummaryDto>,
    // Defaulted, matching NodeMeshFactory/RoomView/DiagramMiniature's own optional-parameter
    // shape. `mountRoomRoute.ts` passes the room's own already-preloaded library so a miniature's
    // model-backed nodes render as the diagram, not a box (SPEC-191) — see DiagramMiniature.ts's
    // own constructor doc for the headset report this fixes.
    private readonly models: ModelLibrary = new ModelLibrary(scene),
  ) {
    this.root = new TransformNode('lobby', scene);
    this.root.parent = parent;

    // Miniatures stand at about chest height rather than on the floor, so the near arc is
    // something you look at rather than down at.
    this.root.position.y = 1.1;
  }

  setCatalog(types: NodeTypeDto[]): void {
    this.catalog = types;

    for (const miniature of this.built.values()) {
      miniature.setCatalog(types);
    }
  }

  /** The ordered list from `GET /api/diagrams/lobby` — occupied first, then by activity. */
  setDiagrams(diagrams: LobbyDiagramDto[]): void {
    this.diagrams = diagrams;
    this.rotation = clampRotation(this.rotation, diagrams.length);
    this.refresh();
  }

  get rankCount(): number {
    return rankCount(this.diagrams.length);
  }

  get currentRotation(): number {
    return this.rotation;
  }

  /**
   * Brings a rank forward (`+1`) or back (`-1`).
   *
   * Returns whether anything moved, so a caller driving this from a thumbstick can tell the
   * difference between "rotated" and "already at the end" without re-deriving the bounds.
   */
  rotate(by: number): boolean {
    const next = clampRotation(this.rotation + by, this.diagrams.length);

    if (next === this.rotation) {
      return false;
    }

    this.rotation = next;
    this.refresh();
    return true;
  }

  /** The diagram ids currently built — what a test asserts on to prove the bound holds. */
  get builtDiagramIds(): string[] {
    return [...this.built.keys()];
  }

  setEnabled(enabled: boolean): void {
    this.root.setEnabled(enabled);
  }

  /**
   * Where a built miniature stands, in world space, or null if it is not built.
   *
   * Forced (`computeWorldMatrix(true)`) at this read site rather than trusted: an unforced call
   * can return last frame's answer, because it skips recomputing once already done for the
   * scene's current render id — and that id only advances during Babylon's own per-frame
   * processing, after `onBeforeRenderObservable` fires. Input runs on that observable, so the
   * unforced version would aim at where a miniature was before the last rotation. Forcing at the
   * read propagates through parent recursion, which is why it belongs here and not at the write
   * (CLAUDE.md).
   */
  centreOf(diagramId: string): Vector3 | null {
    const miniature = this.built.get(diagramId);

    if (miniature === undefined) {
      return null;
    }

    miniature.root.computeWorldMatrix(true);
    return miniature.root.getAbsolutePosition().clone();
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;

    // Labels first: `disposePlate` frees each plate's own material and texture along with the
    // mesh (CLAUDE.md, "That same cascade does not free materials") — a label is parented to its
    // miniature's `root`, so leaving this until after `miniature.dispose()` would let the cascade
    // free the mesh for us and still leak the material/texture behind it.
    for (const label of this.labels.values()) {
      disposePlate(label);
    }

    for (const miniature of this.built.values()) {
      miniature.dispose();
    }

    this.built.clear();
    this.labels.clear();
    this.summaries.clear();
    this.pending.clear();
    this.root.dispose();
  }

  /**
   * Places every slot, builds the ones that need detail, and tears down the ones that no longer
   * do.
   *
   * **Tearing down is not an optimisation here; it is the mechanism.** Without it, rotating
   * through a hundred diagrams would build a hundred miniatures one rank at a time and keep every
   * one of them — the bound would hold at any instant and fail over a session, which is the
   * hardest version of this bug to notice.
   */
  private refresh(): void {
    if (this.disposed) {
      return;
    }

    const slots = layout(this.diagrams.length, this.rotation);
    const wanted = new Set<string>();

    for (const slot of slots) {
      const diagram = this.diagrams[slot.index];

      if (diagram === undefined) {
        continue;
      }

      if (!slot.detailed) {
        continue;
      }

      wanted.add(diagram.id);

      let miniature = this.built.get(diagram.id);

      if (miniature === undefined) {
        miniature = new DiagramMiniature(this.scene, this.root, diagram.id, this.models);
        miniature.setCatalog(this.catalog);
        this.built.set(diagram.id, miniature);

        // The name label (SPEC-201) — built from the lobby list's own `name`, which is already
        // in hand here with no fetch of its own, so the name appears immediately rather than
        // waiting on the summary the miniature's own geometry needs. Parented to the miniature's
        // `root`, not to `this.root` directly: `root`'s own position/scaling (set by `place()`,
        // below) already carries the per-slot placement and the "further ranks are smaller"
        // shrink, so the label inherits both for free instead of this file re-deriving them a
        // second time. `buildLabelledPlate` is the shared -Z/DOUBLESIDE-safe construction
        // (CLAUDE.md, "A Babylon plane's front face is -Z") — `PaletteUI`'s own plates already
        // delegate to it, and this is the reason ExitSign's own module doc gives for reaching for
        // it too, rather than a hand-rolled plane.
        const label = buildLabelledPlate(
          this.scene,
          `lobby-label-${diagram.id}`,
          diagram.name,
          LABEL_BACKGROUND,
          LABEL_INK,
          LABEL_WIDTH,
          LABEL_HEIGHT,
        );
        label.parent = miniature.root;
        label.isPickable = false;
        label.position.set(0, 0.5 + LABEL_GAP + LABEL_HEIGHT / 2, 0);
        this.labels.set(diagram.id, label);

        const summary = this.summaries.get(diagram.id);

        if (summary !== undefined) {
          miniature.build(summary);
        } else {
          void this.load(diagram.id);
        }
      }

      miniature.place(slot.x, slot.y * NEAR_HEIGHT_METRES, slot.z, slot.scale * NEAR_HEIGHT_METRES);

      // The label's own yaw, recomputed every refresh rather than only at build time: rotating a
      // rank forward or back moves a diagram to a new angle in the arc (a diagram's own `built`
      // instance is retained across a rotation, per the wanted-set logic below — SLOTS_PER_RANK
      // 3 keeps the same three diagrams in view for exactly one rotation before any of them
      // retires), so a label built for its original slot would keep facing the angle it was born
      // at. `buildLabelledPlate`'s front is -Z (CLAUDE.md's own billboard entry); with the label's
      // parent (`miniature.root`) carrying no rotation of its own, this is the same
      // `atan2(x, z)` a viewer standing at the arc's own centre (the lobby's world origin, which
      // is where `LobbyInput`'s own aim maths already assumes a person stands) would need to look
      // back at this slot — equivalent to `LabelRenderer.update()`'s per-frame
      // `atan2(-toEye.x, -toEye.z)` with `eye` fixed at the origin, done once here rather than
      // every frame, since a lobby label does not billboard.
      const label = this.labels.get(diagram.id);

      if (label !== undefined) {
        (label.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
          Quaternion.FromEulerAngles(0, Math.atan2(slot.x, slot.z), 0),
        );
      }
    }

    for (const [diagramId, miniature] of [...this.built]) {
      if (!wanted.has(diagramId)) {
        const label = this.labels.get(diagramId);

        if (label !== undefined) {
          disposePlate(label);
          this.labels.delete(diagramId);
        }

        miniature.dispose();
        this.built.delete(diagramId);
      }
    }
  }

  /**
   * Fetches one summary and builds it, if the miniature is still wanted by the time it arrives.
   *
   * The staleness check is the point: rotating twice quickly can retire a miniature while its own
   * fetch is still in flight, and building into a disposed node throws. Checked against the live
   * map rather than a captured reference, so the answer is about now rather than about when the
   * request went out.
   */
  private load(diagramId: string): Promise<void> {
    const existing = this.pending.get(diagramId);

    if (existing !== undefined) {
      return existing;
    }

    const request = this.fetchSummary(diagramId)
      .then((summary) => {
        this.summaries.set(diagramId, summary);

        const miniature = this.built.get(diagramId);

        if (miniature !== undefined) {
          miniature.build(summary);
        }
      })
      .catch(() => {
        // A miniature that cannot be fetched stays an empty slot rather than taking the lobby
        // down. The diagram is still listed, still named, and still openable — the picker's job
        // is to let you reach it, and it can do that without having drawn it.
      })
      .finally(() => {
        this.pending.delete(diagramId);
      });

    this.pending.set(diagramId, request);
    return request;
  }
}
